import { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Checkbox,
  TextField,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { organizationService } from '../../services'
import { formatOrganizationLabel } from '../../utils/organizationLabel'
import { ORG_SEARCH_DEBOUNCE_MS } from './constants'

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />

/**
 * Multi-shop Autocomplete with server-side search.
 * Keeps already-selected shops visible when search results change.
 */
export default function NegativeSalesShopFilter({
  selectedOrgs,
  onChange,
  enabled = true,
}) {
  const [organizations, setOrganizations] = useState([])
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [orgSearchText, setOrgSearchText] = useState('')
  const [orgInputValue, setOrgInputValue] = useState('')

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        setOrgsLoading(true)
        const { items } = await organizationService.getOrganizationsPage({
          page: 1,
          limit: 20,
          search: orgSearchText.trim() || undefined,
        })
        if (!cancelled) setOrganizations(Array.isArray(items) ? items : [])
      } catch (e) {
        console.error('Error fetching organizations:', e)
        if (!cancelled) setOrganizations([])
      } finally {
        if (!cancelled) setOrgsLoading(false)
      }
    }, ORG_SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [orgSearchText, enabled])

  const orgOptions = useMemo(() => {
    const fromSearch = organizations.map((org) => ({
      id: String(org.id),
      label: formatOrganizationLabel(org),
    }))
    const selectedMissing = selectedOrgs.filter(
      (selected) => !fromSearch.some((option) => option.id === selected.id),
    )
    return [...selectedMissing, ...fromSearch]
  }, [organizations, selectedOrgs])

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      sx={{ flex: '1 1 320px', minWidth: 280, maxWidth: 560 }}
      loading={orgsLoading}
      options={orgOptions}
      filterOptions={(options) => options}
      inputValue={orgInputValue}
      value={selectedOrgs}
      onChange={(_, options) => {
        onChange(options || [])
        setOrgInputValue('')
        setOrgSearchText('')
      }}
      onInputChange={(_, value, reason) => {
        if (reason === 'input') {
          setOrgInputValue(value || '')
          setOrgSearchText(value || '')
        } else if (reason === 'clear') {
          onChange([])
          setOrgInputValue('')
          setOrgSearchText('')
        }
        // Ignore "reset" so async option updates do not overwrite typed text.
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.label || ''}
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props
        return (
          <li key={key} {...rest}>
            <Checkbox
              icon={checkboxIcon}
              checkedIcon={checkboxCheckedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Shop"
          placeholder={
            selectedOrgs.length === 0
              ? 'All shops — search to filter'
              : 'Search shop'
          }
        />
      )}
    />
  )
}
